import type { AssetKind, CrawlItemKind, CrawlItemStatus, CrawlItemSubject, EpisodeStatus } from '@zyx/shared';

export const ITEM_STATUS_LABELS: Readonly<Record<CrawlItemStatus, string>> = {
  pending: 'Waiting',
  claimed: 'Running',
  done: 'Done',
  failed: 'Retrying',
  dead: 'Gave up',
  cancelled: 'Cancelled',
};

export const ITEM_KIND_LABELS: Readonly<Record<CrawlItemKind, string>> = {
  list_page: 'Listing page',
  movie_page: 'Movie page',
  episode_check: 'Stream check',
  asset: 'File',
  category_index: 'Category index',
  walk_step: 'Walk step',
};

const ASSET_LABELS: Readonly<Record<AssetKind, string>> = {
  cover: 'Cover',
  preview_video: 'Hover clip',
  preview_image: 'Preview image',
  preview_sprite: 'Scene thumbnails',
  preview_vtt: 'Scene index',
  photo: 'Photo',
};

/** Only the results worth saying; `ok` and `unchecked` go without saying. */
const EPISODE_PROBLEMS: Readonly<Partial<Record<EpisodeStatus, string>>> = {
  cdn_unresolvable: 'video host gone',
  gone: 'removed',
  error: 'did not play',
};

/** `30 KB`, `1.2 MB`, `3.4 GB`. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  }
  return `${(bytes / 1024 / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 * 1024 ? 1 : 0)} GB`;
}

/** `720p` from `['1280x720', '854x480']`: the best one. */
function bestResolution(resolutions: ReadonlyArray<string>): string | undefined {
  const heights = resolutions.map((resolution) => Number(resolution.split('x')[1])).filter((height) => Number.isFinite(height) && height > 0);
  return heights.length === 0 ? undefined : `${Math.max(...heights)}p`;
}

/**
 * What an item is, past its kind and movie code: "Episode 1 · sadie-shop.site · 720p ·
 * 41 min", "Cover · 30 KB". Undefined when the catalogue has nothing to add, as for a
 * listing page, whose URL says it all.
 */
export function describeSubject(kind: CrawlItemKind, subject: CrawlItemSubject | undefined): string | undefined {
  if (subject === undefined) {
    return undefined;
  }
  const parts: string[] = [];
  if (kind === 'asset' && subject.assetKind !== undefined) {
    const many = subject.assetKind === 'preview_sprite' || subject.assetKind === 'preview_image';
    parts.push(many ? `${ASSET_LABELS[subject.assetKind]} ${(subject.assetSeq ?? 0) + 1}` : ASSET_LABELS[subject.assetKind]);
    if (subject.personName !== undefined) {
      parts.push(subject.personName);
    }
    if (subject.episodeNo !== undefined) {
      parts.push(`episode ${subject.episodeNo}`);
    }
    if (subject.bytes !== undefined) {
      parts.push(formatBytes(subject.bytes));
    }
  } else if (kind === 'episode_check' && subject.episodeNo !== undefined) {
    parts.push(`Episode ${subject.episodeNo}`);
    if (subject.playerHost !== undefined) {
      parts.push(subject.playerHost);
    }
    const resolution = bestResolution(subject.resolutions ?? []);
    if (resolution !== undefined) {
      parts.push(resolution);
    }
    if (subject.durationS !== undefined) {
      parts.push(`${Math.round(subject.durationS / 60)} min`);
    }
    const problem = subject.episodeStatus === undefined ? undefined : EPISODE_PROBLEMS[subject.episodeStatus];
    if (problem !== undefined) {
      parts.push(problem);
    }
  }
  return parts.length === 0 ? undefined : parts.join(' · ');
}

/** "in 3 minutes", for a time ahead. */
export function formatIn(ms: number, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.round((ms - now) / 1000));
  if (seconds < 60) {
    return seconds <= 1 ? 'any moment' : `in ${seconds} seconds`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const hours = Math.round(minutes / 60);
  return `in ${hours} hour${hours === 1 ? '' : 's'}`;
}

/**
 * Where an item is with its tries, in words: "succeeded on the first try", "failed 2
 * times, trying again in 2 minutes". `attempts` counts the tries of the current round
 * only; a later crawl of the same URL starts again from none.
 */
export function describeTries(item: { readonly status: CrawlItemStatus; readonly attempts: number; readonly nextAttemptAt?: number }, now: number = Date.now()): string {
  const { attempts } = item;
  const times = `${attempts} time${attempts === 1 ? '' : 's'}`;
  switch (item.status) {
    case 'done':
      return attempts <= 1 ? 'succeeded on the first try' : `succeeded on try ${attempts}`;
    case 'claimed':
      return attempts <= 1 ? 'running now' : `running now, try ${attempts}`;
    case 'failed':
      // A run cut short by a restart goes back without counting as a try.
      if (attempts === 0) {
        return `interrupted, running again ${item.nextAttemptAt === undefined ? 'soon' : formatIn(item.nextAttemptAt, now)}`;
      }
      return `failed ${times}, trying again ${item.nextAttemptAt === undefined ? 'later' : formatIn(item.nextAttemptAt, now)}`;
    case 'dead':
      return `gave up after ${attempts} tr${attempts === 1 ? 'y' : 'ies'}`;
    case 'pending':
      return attempts === 0 ? 'waiting its turn' : `waiting, failed ${times} so far`;
    case 'cancelled':
      return 'cancelled with its job';
  }
}
